import React, { FC, useCallback, useState, useMemo } from 'react'
import { graphql } from 'react-relay'
import { object, string } from 'yup'

import { useNavigate, useLocation, Location } from 'react-router-dom'

import {
  Form,
  FormField,
  FormSubmitButton,
  FormTextInput,
  Stack,
  Text,
  Surface,
  Link,
  LinkText,
  EmojiIcon,
} from '@components'

import { useAuthContext } from '@data/useAuthContext'
import { useMutation } from '@data/useMutation'

import {
  SignInScreenEmailPasswordFormMutation,
  SignInScreenEmailPasswordFormMutationResponse,
} from './__generated__/SignInScreenEmailPasswordFormMutation.graphql'

const VALIDATION_SCHEMA = object().shape({
  email: string().required('Required'),
  password: string().required('Required'),
})

const SIGN_IN_WITH_EMAIL_PASSWORD_MUTATION = graphql`
  mutation SignInScreenEmailPasswordFormMutation(
    $input: SignInWithEmailPasswordMutationInput!
  ) {
    signInWithEmailPassword(input: $input) {
      __typename

      ... on SignInWithEmailPasswordMutationSuccess {
        accessToken
        refreshToken
        query {
          ...AuthenticatedHomeScreen_query

          ...DynamicIslandProfile_query

          viewer {
            id
            ...UserPrimaryPhoto_user
          }
        }
      }
    }
  }
`

export const SignInScreenEmailPasswordForm: FC<{
  initialEmail?: string
  setEmail?: (email: string) => any
  onSubmit?: (result: SignInScreenEmailPasswordFormMutationResponse) => void
}> = ({ initialEmail = '', setEmail, onSubmit }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const { setAuthTokens } = useAuthContext()
  const signInWithEmailPasswordMutation =
    useMutation<SignInScreenEmailPasswordFormMutation>(
      SIGN_IN_WITH_EMAIL_PASSWORD_MUTATION
    )

  const [formError, setFormError] =
    useState<SignInScreenEmailPasswordFormMutationResponse | null>(null)

  const onEmailChange = useCallback(
    (value) => {
      if (setEmail) {
        setEmail(value)
      }
    },
    [setEmail]
  )

  const handleSubmit = useCallback(
    async (values, actions) => {
      setFormError(null)

      const result = await signInWithEmailPasswordMutation({
        variables: {
          input: { email: values.email, password: values.password },
        },

        // Unfortunately Relay doesn't know that the `viewer` on this mutation is
        // the same as the `viewer` on the `Query` root. Therefore we tell Relay.
        updater(store) {
          const payload = store.getRootField('signInWithEmailPassword')

          if (
            payload &&
            payload.getValue('__typename') ===
              'SignInWithEmailPasswordMutationSuccess'
          ) {
            const query = payload.getLinkedRecord('query')

            if (query) {
              const root = store.getRoot()

              root.copyFieldsFrom(query)
            }
          }
        },
      })

      if (
        result.signInWithEmailPassword.__typename !==
        'SignInWithEmailPasswordMutationSuccess'
      ) {
        setFormError(result)
        return
      }

      await setAuthTokens(
        result.signInWithEmailPassword.accessToken,
        result.signInWithEmailPassword.refreshToken
      )

      const authRedirectPath = location.state?.authRedirectPath as string
      navigate(authRedirectPath ?? '/')

      if (onSubmit) {
        onSubmit(result)
      }
    },
    [onSubmit, setFormError, setAuthTokens]
  )

  return (
    <Form
      initialValues={{ email: initialEmail, password: '' }}
      onSubmit={handleSubmit}
      validationSchema={VALIDATION_SCHEMA}
    >
      <Stack gap={6}>
        <FormError result={formError} location={location} />

        <FormField name='email' label='E-mail'>
          <FormTextInput
            autoFocus
            placeholder='Enter your e-mail address'
            keyboardType='email-address'
            submitBehaviour='submit-form'
            onChange={onEmailChange}
          />
        </FormField>

        <FormField name='password' label='Password'>
          <FormTextInput submitBehaviour='submit-form' secureTextEntry />
        </FormField>

        <FormSubmitButton align='flex-end'>Sign In</FormSubmitButton>
      </Stack>
    </Form>
  )
}

const FormError: FC<{
  result: SignInScreenEmailPasswordFormMutationResponse | null
  location: Location
}> = ({ result, location }) => {
  const routerState = useMemo(
    () => ({
      state: {
        backgroundLocation:
          (location.state?.backgroundLocation as Location) || location,

        authRedirectPath: location.state?.authRedirectPath as string,
      },
    }),
    [location]
  )

  if (result === null) {
    return null
  }

  return (
    <Surface variant='errorBox'>
      <Stack gap={2}>
        <Stack gap={3} direction='horizontal' alignItems='center'>
          <EmojiIcon size={8} name='man-shrugging' />

          <Text variant='errorBoxText'>
            We couldn't find an account with that email and password
            combination!
          </Text>
        </Stack>

        <Link to='/sign-in?method=email' options={routerState}>
          <LinkText variant='errorBoxText'>
            Did you want to try using your email instead?
          </LinkText>
        </Link>

        <Text variant='errorBoxText'>
          Once you're in, head over to User Settings to reset your password.
        </Text>
      </Stack>
    </Surface>
  )
}
